/**
 * Category article sections ("About this collection", 20260813 migration).
 *
 * `categories.article` is a jsonb array of { heading?, body } written in the
 * admin Categories tab. Body is PLAIN TEXT — blank lines split paragraphs,
 * renderers escape everything (no parser, no XSS surface). This validator is
 * the single source of the shape rule on the JS side; inc/seo-content.php
 * applies the identical rule server-side so the crawler block and the visible
 * About section can never disagree about what renders.
 *
 * Anything malformed — the whole value or one section — yields nothing rather
 * than breaking the page: the column is hand-applied jsonb (no migration
 * pipeline), so a bad edit must degrade to absence.
 */
import { escapeHtml } from '../../assets/js/utils.js';

// A runaway article must not balloon the page (mirror of the PHP cap).
const MAX_SECTIONS = 12;

/**
 * Validate a raw article value into renderable sections.
 * @param {unknown} raw - the `article` column value (or anything else)
 * @returns {{ heading: string, paragraphs: string[] }[]} heading '' = none
 */
export function articleSections(raw) {
  if (!Array.isArray(raw)) return [];
  const sections = [];
  for (const section of raw) {
    if (sections.length >= MAX_SECTIONS) break;
    if (!section || typeof section !== 'object' || Array.isArray(section)) continue;
    if (typeof section.body !== 'string' || !section.body.trim()) continue;
    const paragraphs = section.body
      .trim()
      .split(/\r?\n[ \t]*(?:\r?\n)+/)
      .map((p) => p.trim())
      .filter(Boolean);
    if (!paragraphs.length) continue;
    sections.push({
      heading: typeof section.heading === 'string' ? section.heading.trim() : '',
      paragraphs,
    });
  }
  return sections;
}

// Labeled [text](https://…) first — it contains a bare URL inside itself —
// then bare http(s) URLs. Nothing else ever becomes markup. The bare-URL
// class also excludes smart quotes: the founder writes on an iPad, whose
// keyboard curls quotes, and a curled quote is never a legal raw URL char.
const LINK_PATTERN = /\[([^\]\r\n]{1,200})\]\((https?:\/\/[^\s)]+)\)|https?:\/\/[^\s<>"'“”‘’)]+/g;

/**
 * One article paragraph → HTML: everything escaped, except that a bare
 * http(s) URL and the [label](url) form render as links (2026-08-13,
 * founder request). A javascript: URL matches neither pattern and stays
 * inert text. MUST MATCH inc/seo-content.php yoink_seo_article_linkify —
 * the crawler block and the visible section render the same text.
 * @param {string} text
 * @returns {string} HTML
 */
export function articleParagraphHtml(text) {
  const s = String(text);
  let out = '';
  let last = 0;
  LINK_PATTERN.lastIndex = 0;
  for (let m = LINK_PATTERN.exec(s); m; m = LINK_PATTERN.exec(s)) {
    out += escapeHtml(s.slice(last, m.index));
    let label;
    let url;
    let tail = '';
    if (m[2]) {
      label = m[1].trim();
      url = m[2];
    } else {
      // Trailing sentence punctuation belongs to the prose, not the URL
      url = m[0].replace(/[.,;:!?]+$/, '');
      label = url;
      tail = m[0].slice(url.length);
    }
    out += `<a href="${escapeHtml(url)}" target="_blank" rel="noopener">${escapeHtml(label)}</a>${escapeHtml(tail)}`;
    last = m.index + m[0].length;
  }
  out += escapeHtml(s.slice(last));
  return out;
}
